import type React from 'react';
import { useState } from 'react';
import { FormField, FormHelp, Label, Textarea } from '@zillow/constellation-10';
import { css } from '@/styled-system/css';

import type { Section } from '../../../models/index';
import YesAndNoExpansion from '../../Widgets/YesAndNoExpansion';
import { PRE_OCCUPANCY_AGREEMENT } from '../../../constants';

type Props = {
  section: Section;
  setSectionValues: (keyValuePairs: object) => void;
};

const MAX_LENGTH = 2000;

const INITIAL_STATE = {
  [PRE_OCCUPANCY_AGREEMENT.FIELD_NAME.IS_ENABLED]: false,
  [PRE_OCCUPANCY_AGREEMENT.FIELD_NAME.PROJECT_DESCRIPTION]: '',
};

export default function PreOccupancyAgreement({ section, setSectionValues }: Props): JSX.Element {
  const [isFocused, setIsFocused] = useState(false);

  const value: string = section.getFieldValue(PRE_OCCUPANCY_AGREEMENT.FIELD_NAME.PROJECT_DESCRIPTION) ?? '';
  const isOverLimit = value.length > MAX_LENGTH;
  const showCounter = isFocused || isOverLimit;

  function onNoAgreedWork(): void {
    setSectionValues(INITIAL_STATE);
  }

  function onYesAgreedWork(): void {
    setSectionValues({
      [PRE_OCCUPANCY_AGREEMENT.FIELD_NAME.IS_ENABLED]: true,
    });
  }

  function onProjectDescription(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void {
    const { value } = event.currentTarget;

    setSectionValues({ [PRE_OCCUPANCY_AGREEMENT.FIELD_NAME.PROJECT_DESCRIPTION]: value });
  }

  return (
    <FormField
      required
      label={<Label>Have you agreed to do any work on the property before the tenant moves in?</Label>}
      control={
        <YesAndNoExpansion
          yesHandler={onYesAgreedWork}
          noHandler={onNoAgreedWork}
          yesActive={section.getFieldValue(PRE_OCCUPANCY_AGREEMENT.FIELD_NAME.IS_ENABLED)}
        >
          <FormField
            required
            error={isOverLimit}
            label={<Label id="project-description">Describe the projects to be done</Label>}
            control={
              <Textarea
                aria-labelledby="project-description"
                onChange={onProjectDescription}
                value={value}
                css={{ resize: 'vertical' }}
                onFocus={() => setIsFocused(true)}
                onBlur={() => setIsFocused(false)}
              />
            }
            description={
              <FormHelp css={showCounter ? { display: 'flex' } : undefined}>
                <span className={css({ flex: 1 })}>
                  {isOverLimit ? `Description must be ${MAX_LENGTH.toLocaleString()} characters or fewer` : ''}
                </span>
                {showCounter && (
                  <span
                    className={css({
                      whiteSpace: 'nowrap',
                      flexShrink: 0,
                      marginLeft: 'auto',
                      paddingLeft: 'tighter',
                    })}
                  >
                    {value.length.toLocaleString()}/{MAX_LENGTH.toLocaleString()} characters
                  </span>
                )}
              </FormHelp>
            }
          />
        </YesAndNoExpansion>
      }
    />
  );
}
